import axios from 'axios';

const BASE_URL = window._env_!.REACT_APP_API_BASE!;

interface ContactRequestBody {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
}

/**
 * Sends a contact request to the server.
 *
 * @param contactData - The data to be sent in the contact request.
 * @returns A promise that resolves to the response data from the server.
 * @throws Will throw an error if the request fails.
 */
export const sendContactRequest = async (contactData: ContactRequestBody) => {
  try {
    const response = await axios.post(`${BASE_URL}/contacts`, contactData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error sending contact request:', error);
    throw error;
  }
};

/**
 * Fetches a paginated list of products from the API, optionally filtered by category.
 *
 * @param params - The parameters for fetching products.
 * @param params.category - (Optional) The category to filter products by. If "all" or undefined, no category filter is applied.
 * @param params.page - (Optional) The page number for pagination. Defaults to the first page if not provided.
 * @returns A promise that resolves to the Axios response containing the list of products.
 */
export const fetchProducts = async ({
  category,
  page
}: {
  category?: string;
  page?: number;
}) => {
  const filters = [];
  if (category && category !== "all") {
    filters.push(`filter=category eq '${category}'`);
  }

  const params = [
    ...filters,
    `limit=8`,
    `offset=${page ? page * 8 : 0}`,
    `orderby=name asc`
  ].join("&");
  const response = await axios.get(`${BASE_URL}/products?${params}`);
  return response.data
};

/**
 * Fetches a product by its unique identifier from the API without authentication.
 *
 * @param productId - The unique identifier of the product to fetch.
 * @returns A promise that resolves to the product data.
 *
 * @throws Will throw an error if the request fails.
 */
export const fetchProductById = async (productId: string) => {
  const response =  await axios.get(`${BASE_URL}/products/${productId}`);
  return response.data
};